import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useState } from "react";
import { Logo } from "@/components/shared/Logo";
import { ThemeSwitcher } from "@/components/shared/ThemeSwitcher";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const Footer = () => {
  const { t } = useTranslation();
  const [missionTooltipOpen, setMissionTooltipOpen] = useState(false);

  return (
    <footer className="relative py-16 px-4 bg-gradient-footer text-white overflow-hidden">
      {/* Gradient Overlay for extra depth */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-teal/20 via-transparent to-brand-purple/20 pointer-events-none" />
      
      <div className="container mx-auto max-w-6xl relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {/* Column 1: About */}
          <div>
            <h4 className="font-clash font-semibold text-lg mb-4">
              {t("footer.links.about")}
            </h4>
            <ul className="space-y-2">
              <li>
                <TooltipProvider>
                  <Tooltip open={missionTooltipOpen} onOpenChange={setMissionTooltipOpen}>
                    <TooltipTrigger asChild>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setMissionTooltipOpen(!missionTooltipOpen);
                        }}
                        className="text-sm text-white/80 hover:text-white transition-colors duration-150"
                      >
                        Our Mission
                      </a>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-sm">
                      <p>{t("footer.tooltip.description")}</p>
                      <p className="italic mt-1 opacity-60">{t("footer.tooltip.hint")}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </li>
              <li>
                <a
                  href="#how-it-works"
                  className="text-sm text-white/80 hover:text-white transition-colors duration-150"
                >
                  How It Works
                </a>
              </li>
              <li>
                <a
                  href="#whos-behind"
                  className="text-sm text-white/80 hover:text-white transition-colors duration-150"
                >
                  Team
                </a>
              </li>
            </ul>
          </div>

          {/* Column 2: Resources */}
          <div>
            <h4 className="font-clash font-semibold text-lg mb-4">Resources</h4>
            <ul className="space-y-2">
              <li>
                <span className="text-sm text-white/80">
                  Blog (Coming soon)
                </span>
              </li>
              <li>
                <span className="text-sm text-white/80">
                  Help Centre (Early Access)
                </span>
              </li>
              <li>
                <a
                  href="#faq"
                  className="text-sm text-white/80 hover:text-white transition-colors duration-150"
                >
                  FAQs
                </a>
              </li>
            </ul>
          </div>

          {/* Column 3: Legal */}
          <div>
            <h4 className="font-clash font-semibold text-lg mb-4">Legal</h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/privacy"
                  className="text-sm text-white/80 hover:text-white transition-colors duration-150"
                >
                  {t("footer.links.privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="text-sm text-white/80 hover:text-white transition-colors duration-150"
                >
                  {t("footer.links.terms")}
                </Link>
              </li>
              <li>
                <Link
                  to="/cookies"
                  className="text-sm text-white/80 hover:text-white transition-colors duration-150"
                >
                  {t("footer.links.cookies")}
                </Link>
              </li>
              <li>
                <Link
                  to="/gdpr"
                  className="text-sm text-white/80 hover:text-white transition-colors duration-150"
                >
                  {t("footer.links.gdpr")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Connect */}
          <div>
            <h4 className="font-clash font-semibold text-lg mb-4">
              {t("footer.links.contact")}
            </h4>
            <ul className="space-y-2">
              <li>
                <a
                  href="#"
                  className="text-sm text-white/80 hover:text-white transition-colors duration-150"
                  aria-label="LinkedIn"
                >
                  LinkedIn
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-sm text-white/80 hover:text-white transition-colors duration-150"
                  aria-label="Instagram"
                >
                  Instagram
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar with minimal logo */}
        <div className="pt-8 border-t border-white/20 flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Mobile Switchers */}
          <div className="flex md:hidden items-center gap-1 order-1">
            <ThemeSwitcher />
            <LanguageSwitcher />
          </div>

          <div className="flex items-center gap-4 order-2 md:order-1">
            <Logo showText={false} className="w-8 h-8 opacity-60" />
            <p className="text-xs text-white/70">
              <a
                href="https://momentumlab.earth"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors duration-150"
              >
                {t("footer.copyright")}
              </a>
            </p>
          </div>
          
          <p className="text-xs text-white/70 order-3 md:order-2">
            {t("footer.project")} • {t("footer.madeInEU")}
          </p>
        </div>
      </div>
    </footer>
  );
};
